import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type {
  Funnel2Response,
  OutreachersResponse,
  QualityResponse,
  ScorecardResponse,
  SourcesResponse,
  SpendResponse,
} from '@/api/insights';

/**
 * The Insights tabs' endpoints. Every one takes the shared scope
 * (`dateFrom` / `dateTo` as a half-open ISO window, plus `team` or
 * `excludeTeam`); see InsightsScope in lib/insights.
 */
const KEY = ['admin', 'insights'] as const;
type Params = Record<string, string>;

function useInsightsQuery<T>(endpoint: string, params: Params, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, endpoint, params],
    queryFn: () => api.get<T>(`/admin/analytics/${endpoint}`, params),
    staleTime: 60_000,
    enabled,
  });
}

export function useScorecard(params: Params) {
  return useInsightsQuery<ScorecardResponse>('scorecard', params);
}

export function useFunnel2(params: Params, options: { enabled?: boolean } = {}) {
  return useInsightsQuery<Funnel2Response>('funnel2', params, options.enabled ?? true);
}

export function useOutreacherInsights(params: Params) {
  return useInsightsQuery<OutreachersResponse>('outreachers', params);
}

export function useSpend(params: Params, options: { enabled?: boolean } = {}) {
  return useInsightsQuery<SpendResponse>('spend', params, options.enabled ?? true);
}

export function useSources(params: Params) {
  return useInsightsQuery<SourcesResponse>('sources', params);
}

export function useQuality(params: Params) {
  return useInsightsQuery<QualityResponse>('quality', params);
}
